import { useTranslation } from 'react-i18next'

interface Winner {
  address: string
  place: 1 | 2 | 3
  prize: string
  /** The platform's own ticket (docs/HOUSE_TOPUP_DESIGN.md): named, not shown as a wallet. */
  house?: boolean
}

interface WinnersProps {
  winners: Winner[]
  loading?: boolean
}

export function Winners({ winners, loading }: WinnersProps) {
  const { t } = useTranslation()

  const placeEmoji = (place: number) => {
    switch (place) {
      case 1: return '🥇'
      case 2: return '🥈'
      case 3: return '🥉'
      default: return '🎫'
    }
  }

  const placeLabel = (place: number) => {
    switch (place) {
      case 1: return t('winners.first')
      case 2: return t('winners.second')
      case 3: return t('winners.third')
      default: return ''
    }
  }

  const shortenAddress = (addr: string) => {
    if (!addr) return ''
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`
  }

  const formatPrize = (prize: string) => {
    const num = parseFloat(prize)
    if (isNaN(num)) return '$0.00'
    return `$${num.toFixed(2)}`
  }

  if (loading) {
    return (
      <div className="card">
        <h3 className="text-sm font-medium text-telegram-hint mb-4">{t('winners.recent')}</h3>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full shimmer" />
              <div className="flex-1"><div className="h-4 w-24 shimmer rounded" /></div>
              <div className="h-4 w-16 shimmer rounded" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (winners.length === 0) {
    return (
      <div className="card">
        <h3 className="text-sm font-medium text-telegram-hint mb-4">{t('winners.recent')}</h3>
        <div className="text-center py-6 text-telegram-hint">
          <div className="text-3xl mb-2">🏆</div>
          {t('winners.noWinners')}
          <p className="text-xs mt-2 opacity-80">{t('winners.claimWindow')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <h3 className="text-sm font-medium text-telegram-hint mb-4">{t('winners.recent')}</h3>
      <div className="space-y-3">
        {winners.map((winner, index) => (
          <div key={`${winner.address}-${index}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-telegram-bg">
            <div className="text-2xl">{placeEmoji(winner.place)}</div>
            <div className="flex-1 min-w-0">
              <span className="font-mono text-sm text-telegram-text truncate">{winner.house ? t('winners.house') : shortenAddress(winner.address)}</span>
              <div className="text-xs text-telegram-hint">{placeLabel(winner.place)}</div>
            </div>
            <div className="font-semibold text-gold-500">{formatPrize(winner.prize)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
